import { useLayoutEffect, useState, type RefObject } from 'react';
import {
  computeEvimoTableBodyScrollY,
  type ComputeEvimoTableBodyScrollYOptions,
} from '~/app/components/common/EvimoTable/computeEvimoTableBodyScrollY';

function isAutoScrollBodyDisabled(wrap: HTMLElement): boolean {
  return Boolean(
    wrap.closest('.ant-table-expanded-row') ||
    wrap.closest('[data-evimo-table-scroll-body="off"]'),
  );
}

type UseEvimoTableBodyScrollYOptions = {
  enabled: boolean;
  /** Re-measure when pagination or row count changes (DOM layout updates). */
  measureKey?: string | number;
  computeOptions?: ComputeEvimoTableBodyScrollYOptions;
};

export function useEvimoTableBodyScrollY(
  wrapRef: RefObject<HTMLElement | null>,
  { enabled, measureKey, computeOptions }: UseEvimoTableBodyScrollYOptions,
): number | undefined {
  const [scrollY, setScrollY] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!enabled) {
      setScrollY(undefined);
      return;
    }

    const wrap = wrapRef.current;
    if (!wrap) {
      return;
    }

    const update = () => {
      const next = wrapRef.current;
      if (!next) {
        return;
      }
      if (isAutoScrollBodyDisabled(next)) {
        setScrollY(undefined);
        return;
      }
      setScrollY(computeEvimoTableBodyScrollY(next, computeOptions));
    };

    update();
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(update);
    });
    const timeoutId = window.setTimeout(update, 50);

    const observer = new ResizeObserver(() => {
      update();
    });

    observer.observe(wrap);

    const shell = wrap.closest('.landing-app-shell');
    if (shell instanceof HTMLElement) {
      observer.observe(shell);
    }

    const mainScroll = wrap.closest('.landing-content-scroll');
    if (mainScroll instanceof HTMLElement) {
      observer.observe(mainScroll);
    }

    window.addEventListener('resize', update);

    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, measureKey, wrapRef, computeOptions]);

  return enabled ? scrollY : undefined;
}
